import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import { FieldFrame, FieldLayout } from "./FieldFrame";

/**
 * Choix de la ville à partir du code postal, via l'API Adresse de l'État.
 *
 * Partagé par tous les formulaires qui demandent une ville : la saisie libre laissait
 * passer les fautes de frappe et les variantes d'un même nom. Le code postal reste au
 * formulaire appelant — chacun a sa validation et son nommage de champ — et c'est lui,
 * non le texte tapé dans la liste, qui déclenche la recherche. Choisir une ville peut en
 * retour préciser le code postal, d'où les deux valeurs rendues par `onChange`.
 */
type CityOption = {
  value: string;
  label: string;
  codePostal: string;
};

/** L'API ne répond que sur un code postal complet. */
const isSearchable = (postalCode: string) => postalCode.length === 5;

const fetchCities = async (postalCode: string): Promise<CityOption[]> => {
  try {
    const response = await fetch(
      `https://geo.api.gouv.fr/communes?codePostal=${postalCode}&fields=nom,codesPostaux`
    );
    const data = await response.json();

    return (data as { nom: string }[]).map((city) => ({
      value: city.nom,
      label: city.nom,
      codePostal: postalCode,
    }));
  } catch (error) {
    console.error("Erreur lors de la recherche des villes:", error);
    return [];
  }
};

export const CityField = ({
  zipCode,
  value,
  onChange,
  label,
  layout = "stacked",
  required,
  disabled,
  error,
}: {
  /** Code postal saisi dans le formulaire : c'est lui qui déclenche la recherche. */
  zipCode: string;
  value: string;
  /** Reçoit la ville ET le code postal, qu'un choix de ville peut préciser. */
  onChange: (city: string, zipCode: string) => void;
  label?: string;
  layout?: FieldLayout;
  required?: boolean;
  disabled?: boolean;
  error?: string;
}) => {
  const { t } = useTranslation();
  const [options, setOptions] = useState<CityOption[]>([]);
  const [loading, setLoading] = useState(false);

  // En ref : la recherche ne doit pas dépendre de l'identité du callback. Une fonction
  // recréée à chaque rendu par le formulaire appelant la relancerait en boucle.
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const valueRef = useRef(value);
  valueRef.current = value;

  useEffect(() => {
    // Désactivé, le champ ne cherche rien et surtout n'émet rien : la sélection d'office
    // ci-dessous remonterait une modification que l'utilisateur n'a pas faite et ne peut
    // pas enregistrer — sur « Mon compte », elle déclenchait la barre d'enregistrement
    // d'un collaborateur qui n'a pas le droit de modifier son entreprise.
    if (disabled || !isSearchable(zipCode)) {
      setOptions([]);
      return;
    }

    let cancelled = false;
    setLoading(true);
    fetchCities(zipCode).then((found) => {
      if (cancelled) return;
      setOptions(found);
      setLoading(false);
      // Une seule commune pour ce code postal : la choisir d'office épargne un clic qui
      // n'a aucune alternative.
      if (found.length === 1 && found[0].value !== valueRef.current) {
        onChangeRef.current(found[0].value, found[0].codePostal);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [zipCode, disabled]);

  return (
    <FieldFrame
      layout={layout}
      label={label || t("company-city")}
      required={required}
      htmlFor="city"
      error={error}
    >
      <Select
        inputId="city"
        options={options}
        isLoading={loading}
        value={value ? { value, label: value, codePostal: zipCode } : null}
        onChange={(option: CityOption | null) =>
          onChange(option?.value || "", option?.codePostal || zipCode)
        }
        placeholder={t("city-placeholder")}
        noOptionsMessage={() =>
          isSearchable(zipCode) ? t("city-none-found") : t("city-no-options")
        }
        loadingMessage={() => t("city-loading")}
        isDisabled={disabled || !isSearchable(zipCode)}
        classNames={{
          // Désactivé : même gris que les `FormInput` verrouillés, sans quoi le champ
          // reste blanc et paraît saisissable.
          control: (state) =>
            `${layout === "row" ? "!min-h-[30px]" : "!min-h-[42px]"} !border ${
              error ? "!border-red-500" : "!border-grey-medium"
            } !rounded-lg !shadow-none hover:!border-grey-medium focus:!border-tertiary ${
              state.isDisabled
                ? "!bg-grey-light/60 !cursor-not-allowed"
                : "!bg-white"
            }`,
          // Mode ligne : les marges par défaut de react-select dépasseraient la hauteur
          // des autres champs compacts.
          valueContainer: () => (layout === "row" ? "!py-0 !px-2" : ""),
          dropdownIndicator: () => (layout === "row" ? "!py-0 !px-1" : ""),
          placeholder: () => "!text-grey-medium",
          input: () => "!text-sm",
          option: () => "!text-sm hover:!bg-grey-light !text-tertiary",
          menu: () => "!rounded-lg !shadow-md",
          singleValue: () =>
            disabled
              ? "!text-sm !text-grey-readable"
              : "!text-sm !text-tertiary",
        }}
      />
    </FieldFrame>
  );
};
